/**
 * Tenant-wide master switches for notifications.
 *
 * The email switch is a kill switch: with it off, the emit pipeline rejects the
 * `email` and `digest` channels for every recipient of this tenant before
 * anything reaches the email queue, whatever the per-event configuration below
 * says. Bells keep working. Switching either kill switch off asks for
 * confirmation first and applies immediately — these are the controls you reach
 * for during an incident, so they must not sit behind the grid's Save button.
 *
 * The default frequency lives here too: it is the one control that moves every
 * event at once, so it belongs with the other tenant-wide settings rather than
 * buried in the grid.
 */
import { useState } from 'react';
import type {
  NotificationSettingsUpdate,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { Badge } from '../../../ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../ui/alert-dialog';
import { Bell, Eye, Loader2, Mail, MailX, Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import { notificationsAPI } from '../../../../lib/api';
import { Button } from '../../../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../ui/dialog';
import { NotificationDefaultFrequency } from './NotificationDefaultFrequency';
import type { FrequencySummary, MasterSwitches, SetupChoice } from './types';

/** Which switch the confirmation dialog is currently guarding. */
type GuardedSwitch = 'emails' | 'bell';

type DigestPreview = { subject: string; html: string };

const GUARD_COPY: Record<GuardedSwitch, { title: string; body: string; confirm: string }> = {
  emails: {
    title: 'Stop all notification email for this tenant?',
    body:
      'No notification email or digest will be sent to anyone in this tenant — '
      + 'customer or supplier — while this is off. Per-event email settings, '
      + 'per-user subscriptions and digests are all overridden. In-app bells '
      + 'keep working, so nothing is lost, but nobody is emailed about a late '
      + 'delivery, a dispute or an expiring document until you switch it back '
      + 'on. Invitations and password resets are unaffected.',
    confirm: 'Stop all notification email',
  },
  bell: {
    title: 'Stop all in-app notifications for this tenant?',
    body:
      'No new in-app notification will be created for anyone in this tenant '
      + 'while this is off. Users keep the notifications they already have, but '
      + 'nothing new appears in the bell until you switch it back on.',
    confirm: 'Stop in-app notifications',
  },
};

interface NotificationMasterCardProps {
  master: MasterSwitches;
  canConfigure: boolean;
  saving: boolean;
  /** Cadence options, straight off the catalogue's `frequency_choices`. */
  frequencyChoices: SetupChoice[];
  /** How many events are pinned to a custom cadence, staged edits included. */
  frequencySummary: FrequencySummary;
  /** Applies a settings patch immediately and toasts `message` on success. */
  onChange: (patch: NotificationSettingsUpdate, message: string) => void;
  /** The dedicated kill-switch write. */
  onEmailsEnabledChange: (enabled: boolean) => void;
  /** Sets the tenant-wide default cadence. Applies immediately. */
  onDefaultFrequencyChange: (frequency: NotificationTenantDefaultFrequency) => void;
}

export function NotificationMasterCard({
  master,
  canConfigure,
  saving,
  frequencyChoices,
  frequencySummary,
  onChange,
  onEmailsEnabledChange,
  onDefaultFrequencyChange,
}: NotificationMasterCardProps) {
  const [guard, setGuard] = useState<GuardedSwitch | null>(null);
  const [preview, setPreview] = useState<DigestPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = (kind: 'procurement' | 'quality' = 'procurement') => {
    setPreviewLoading(true);
    notificationsAPI.getDigestPreview(kind)
      .then((res) => setPreview(res.data))
      .catch((err: unknown) => {
        console.error('Digest preview failed:', err);
        toast.error('Could not load the daily summary preview.');
      })
      .finally(() => setPreviewLoading(false));
  };

  const handleEmails = (enabled: boolean) => {
    if (!enabled) {
      setGuard('emails');
      return;
    }
    onEmailsEnabledChange(true);
  };

  const handleBell = (enabled: boolean) => {
    if (!enabled) {
      setGuard('bell');
      return;
    }
    onChange({ bell_enabled: true }, 'In-app notifications are back on');
  };

  const confirmGuard = () => {
    if (guard === 'emails') {
      onEmailsEnabledChange(false);
    } else if (guard === 'bell') {
      onChange({ bell_enabled: false }, 'In-app notifications are now stopped for this tenant');
    }
    setGuard(null);
  };

  const copy = guard ? GUARD_COPY[guard] : null;
  const disabled = !canConfigure || saving;

  return (
    <>
      <Card className={master.emailsEnabled ? undefined : 'border-destructive'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Master switches
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </CardTitle>
          <CardDescription>
            Tenant-wide settings. The switches win over every per-event setting
            below and over each user&apos;s own preferences; the default frequency
            is what an event uses unless it has been given one of its own.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* MASTER EMAIL KILL SWITCH */}
          <div
            className={`flex items-start justify-between gap-4 rounded-lg border p-4 ${
              master.emailsEnabled ? '' : 'border-destructive bg-destructive/5'
            }`}
          >
            <div className="space-y-1">
              <Label htmlFor="notif-master-emails" className="flex items-center gap-2 text-base font-semibold">
                {master.emailsEnabled ? <Mail className="h-4 w-4" /> : <MailX className="h-4 w-4 text-destructive" />}
                Notification emails
                {!master.emailsEnabled && (
                  <Badge variant="destructive">All notification email stopped</Badge>
                )}
              </Label>
              <p className="text-sm text-muted-foreground">
                {master.emailsEnabled
                  ? 'Emails and digests send according to the per-event settings below.'
                  : 'Nobody in this tenant is being emailed or digested about anything. '
                    + 'In-app bells still work; invitations and password resets are unaffected.'}
              </p>
            </div>
            <Switch
              id="notif-master-emails"
              checked={master.emailsEnabled}
              disabled={disabled}
              onCheckedChange={handleEmails}
            />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-1">
                <Label htmlFor="notif-master-bell" className="flex items-center gap-2 font-medium">
                  <Bell className="h-4 w-4" />
                  In-app notifications
                </Label>
                <p className="text-sm text-muted-foreground">
                  Creates bell notifications for this tenant.
                </p>
              </div>
              <Switch
                id="notif-master-bell"
                checked={master.bellEnabled}
                disabled={disabled}
                onCheckedChange={handleBell}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-1">
                <Label htmlFor="notif-master-digest" className="flex items-center gap-2 font-medium">
                  <Newspaper className="h-4 w-4" />
                  Digest emails
                </Label>
                <p className="text-sm text-muted-foreground">
                  {master.emailsEnabled
                    ? 'Batched summaries for events set to the digest channel.'
                    : 'Already stopped by the email master switch above.'}
                </p>
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7"
                    onClick={() => openPreview('procurement')}
                    disabled={previewLoading}
                  >
                    {previewLoading
                      ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <Eye className="h-3.5 w-3.5 mr-1.5" />}
                    Preview procurement summary
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7"
                    onClick={() => openPreview('quality')}
                    disabled={previewLoading}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    Preview quality summary
                  </Button>
                </div>
              </div>
              <Switch
                id="notif-master-digest"
                checked={master.digestEnabled && master.emailsEnabled}
                disabled={disabled || !master.emailsEnabled}
                onCheckedChange={(checked) =>
                  onChange(
                    { digest_enabled: checked },
                    checked ? 'Digest emails enabled' : 'Digest emails disabled',
                  )
                }
              />
            </div>
          </div>

          <NotificationDefaultFrequency
            value={master.defaultFrequency}
            choices={frequencyChoices}
            summary={frequencySummary}
            disabled={disabled}
            onChange={onDefaultFrequencyChange}
          />
        </CardContent>
      </Card>

      <AlertDialog open={guard !== null} onOpenChange={(open) => !open && setGuard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmGuard}>{copy?.confirm}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={preview !== null} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{preview?.subject ?? 'Daily summary'}</DialogTitle>
            <p className="text-xs text-muted-foreground">
              Rendered with sample data and this install's branding — exactly the
              layout recipients receive.
            </p>
          </DialogHeader>
          {preview && (
            <iframe
              title="Daily summary email preview"
              srcDoc={preview.html}
              sandbox=""
              className="w-full h-[65vh] rounded border bg-white"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
